/* eslint-disable react/jsx-pascal-case */
import React, {useState} from 'react';

import {Button, Input, PasswordInput, Anchor} from '@newsela/angelou';
import {useFormik} from 'formik';
import {startsWith, trim, some, find} from 'lodash-es';
import PropTypes from 'prop-types';

import RegistrationHeaderText from 'components/RegistrationHeaderText';
import CleverAnchor from 'components/SSOAnchors/CleverAnchor';
import GoogleAnchor from 'components/SSOAnchors/GoogleAnchor';
import MicrosoftAnchor from 'components/SSOAnchors/MicrosoftAnchor';
import ErrorToast from 'static/three-oh/src/components/ErrorToast/ErrorToast';
import SessionApi from 'static/three-oh/src/modules/apis/Session/SessionApi';
import {isEmptyValue} from 'static/three-oh/src/modulesV2/utils/registrationUtils.js';

import styles from './styles';

const propTypes = {
  credentialsFromFields: PropTypes.func,
  identifyMarketoLead: PropTypes.func,
  joinUrl: PropTypes.string,
  loading: PropTypes.bool,
  nextUrl: PropTypes.string,
  redirectUser: PropTypes.func,
};

const SignInForm = ({
  credentialsFromFields,
  identifyMarketoLead,
  joinUrl,
  nextUrl,
  redirectUser,
}) => {
  const [showErrorMessage, setErrorMessage] = useState(null);
  const queryParams = new URLSearchParams(window.location.search);
  const classroomIdFromQueryParams = queryParams.get('classroom_id');

  const showError = (error) => {
    const defaultMessage =
      'Incorrect username or password. Password is case sensitive.';

    // Some API errors contain HTML which should be displayed to the user. To avoid XSS or printing
    // out a stack trace, only let through certain error messages that we trust.
    const errorWhiteList = [
      'Please enter a correct username and password',
      'You signed up with Google',
      'You signed up with Clever',
      'You signed up with Microsoft',
      'User inactive or deleted',
      'Staff may not use passwords. Sign on with Google.',
    ];

    const isWhiteListed = (errorMessage) => {
      const trimmedErrorMessage = trim(errorMessage);
      return some(errorWhiteList, (whiteListedMessage) => {
        return startsWith(trimmedErrorMessage, whiteListedMessage);
      });
    };

    const errorMessage = isWhiteListed(error.message)
      ? error.message
      : defaultMessage;
    setErrorMessage(errorMessage);
  };

  const submitSignInForm = (values) => {
    const form = {
      fields: [
        {
          name: 'username',
          value: values.username,
          isInvalid: (val) => {
            return val.length == 0
              ? 'Please enter your email or username.'
              : false;
          },
        },
        {
          name: 'password',
          value: values.password,
          isInvalid: (val) => {
            return val.length == 0 ? 'Please enter your password.' : false;
          },
        },
      ],
    };

    for (const idx in form.fields) {
      const field = form.fields[idx];
      const error = field.isInvalid(field.value || '');
      if (error) {
        form.fields[idx].errorMessage = error;
      }
    }

    const credentials = credentialsFromFields(form.fields);

    SessionApi.signIn(credentials)
      .then(identifyMarketoLead)
      .then(redirectUser)
      .catch(showError);
  };

  const clearBannerErrorOnChange = (values) => {
    if (values) {
      setErrorMessage(null);
    }
  };

  const formik = useFormik({
    initialValues: {
      username: '',
      password: '',
    },
    validate: (values) => {
      clearBannerErrorOnChange(values);
      const errors = {};
      if (isEmptyValue(values.username)) {
        errors.username = 'Please enter your email or username.';
      }

      if (isEmptyValue(values.password) || values.password.length < 6) {
        errors.password = 'Your password should be 6 characters or more';
      }
      return errors;
    },
    onSubmit: (values) => {
      submitSignInForm(values);
    }
  });

  const redirectToSocialProvider = (
    provider,
    nextUrl,
    classroom_id,
    source
  ) => {
    let url = `/login/${provider}/?source=${source}`;
    // We actually go directly to Clever here, instead of using py-social-auth.
    if (provider == 'clever') {
      const client_id = window.CLEVER_OAUTH2_CLIENT_ID;
      const redirect_uri = `${window.location.origin}/clever`;
      const base_url = 'https://clever.com/oauth/authorize?response_type=code';
      url = `${base_url}&client_id=${client_id}&redirect_uri=${redirect_uri}`;
    }
    if (classroom_id) {
      url += `&classroom_id=${classroom_id}`;
    }
    if (nextUrl) {
      const field = provider === 'clever' ? 'state' : 'next';
      url += `&${field}=${encodeURIComponent(nextUrl)}`;
    }
    return url;
  };

  const htmlErrorMessageAsJsx = (errorMessage) => {
    return (
      <span role="alert" data-testid="sign-in-error-msg">
        <span dangerouslySetInnerHTML={{__html: errorMessage}} /> {/* eslint-disable-line react/no-danger */}
      </span>
    );
  };

  const getJoinUrl = () => {
    const href = joinUrl ? `${window.location.origin}${joinUrl}` : window.location.origin;
    const url = new URL(href, window.location);
    if (nextUrl) url.searchParams.append('next', nextUrl);
    return url.href;
  };

  return (
    <div data-qa-selector="signin_form" className={styles.formContainer}>
      {showErrorMessage && (
        <ErrorToast
          errorMessage={htmlErrorMessageAsJsx(showErrorMessage)}
          additionalClassName={styles.errorMessage}
          showCloseButton={false}
        />
      )}
      <RegistrationHeaderText>Sign in to Newsela With</RegistrationHeaderText>
      <div>
        <div className={styles.thirdPartyAnchors}>
          <GoogleAnchor
            actionName="Sign In"
            additionalClassName={styles.socialLink}
            anchorText="Google"
            ariaProps={{
              'aria-label': 'Sign in with Google',
            }}
            href={redirectToSocialProvider(
              'google-oauth2',
              nextUrl,
              classroomIdFromQueryParams,
              'sign-in-without-class-code'
            )}
            iconClassName={styles.googleIcon}
            eventProperties={{description: 'Sign in with Google.'}}
            legacyEventName="User-Sign In"
            streamProperties={{description: 'Sign in with Google.'}}
          />
          <MicrosoftAnchor
            actionName="Sign In"
            additionalClassName={styles.socialLink}
            anchorText="Microsoft"
            ariaProps={{
              'aria-label': 'Sign in with Microsoft',
            }}
            href={redirectToSocialProvider(
              'microsoft-oauth2',
              nextUrl,
              classroomIdFromQueryParams,
              'sign-in'
            )}
            iconClassName={styles.microsoftIcon}
            eventProperties={{description: 'Sign in with Microsoft.'}}
            legacyEventName="User-Sign In"
            streamProperties={{description: 'Sign in with Microsoft.'}}
          />
          <CleverAnchor
            actionName="Sign In"
            additionalClassName={styles.socialLink}
            anchorText="Clever"
            ariaProps={{
              'aria-label': 'Sign in with Clever',
            }}
            href={redirectToSocialProvider(
              'clever',
              nextUrl,
              classroomIdFromQueryParams,
              'sign-in'
            )}
            iconClassName={styles.cleverIcon}
            eventProperties={{description: 'Sign in with Clever.'}}
            legacyEventName="User-Sign In"
            streamProperties={{description: 'Sign in with Clever.'}}
          />
        </div>
      </div>
      <div>
        <RegistrationHeaderText>
          Or use your Newsela account:
        </RegistrationHeaderText>
        <form onSubmit={formik.handleSubmit}>
          <Input
            autocomplete="email"
            __cssFor={{
              root: styles.textInputFocus,
            }}
            dataProps={{'data-qa-selector': 'username'}}
            error={formik.errors.username}
            inputType="text"
            label="Email or username"
            name="username"
            onBlur={formik.handleBlur}
            onChange={formik.handleChange}
            touched={!!formik.touched.username}
            value={formik.values.username}
            underInputId="email-username-input-notification-id"
          />
          <PasswordInput
            allowPasswordVisibility
            __cssFor={{
              root: styles.textInputFocus,
            }}
            dataProps={{'data-qa-selector': 'password'}}
            error={formik.errors.password}
            helperText={
              formik.errors.password ? '' : 'Must be at least 6 characters long'
            }
            name="password"
            label="Password"
            onBlur={formik.handleBlur}
            onChange={formik.handleChange}
            touched={!!formik.touched.password}
            value={formik.values.password}
            underInputId="password-input-notification-id"
            autocomplete="current-password"
          />

          <Button
            ariaProps={{
              'aria-label': 'Sign In',
            }}
            __cssFor={{
              root: `
                width: 100%;
                margin-top: 1.5rem;
                margin-bottom: 1.5rem;
              `,
            }}
            type="submit"
            dataProps={{
              'data-qa-selector': 'sign_in',
            }}
            /* AUTOGENERATED TODO: update angelou to new flavor.
              see https://github.com/newsela/angelou/blob/main/src/components/Button/README.md#MIGRATION
              for migration guide. */
            legacy_flavor={Button.legacy_flavor.solid}
            /* AUTOGENERATED TODO: update angelou to new flavor.
              see https://github.com/newsela/angelou/blob/main/src/components/Button/README.md#MIGRATION
              for migration guide. */
            legacy_statusColor={Button.legacy_statusColor.primary}
          >
            Sign In
          </Button>
        </form>

        <div className={styles.links}>
          <Anchor
            href={`${window.location.origin}/people/password_reset/`}
            dataProps={{'data-qa-selector': 'reset_password'}}
            __cssFor={{
              root: styles.forgotPassword
            }}
          >
            Forgot your password?
          </Anchor>
          <div>
            <span>Not a member yet?</span>
            <Anchor
              __cssFor={{
                root: styles.joinNow
              }}
              dataProps={{'data-qa-selector': 'join'}}
              href={getJoinUrl()}
            >
              Join Now
            </Anchor>
          </div>
        </div>
      </div>
    </div>
  );
};

SignInForm.propTypes = propTypes;
SignInForm.defaultProps = {
  credentialsFromFields: (fields) => ({
    username: find(fields, (field) => field.name === 'username').value,
    password: find(fields, (field) => field.name === 'password').value,
  }),
};

export default SignInForm;
